"use client";

import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { BookOpen } from "lucide-react";
import type { AppJournalEntry } from "@/vybe-trader-client/messaging/stores/useJournalStore";
import { Button } from "@/shadcn-components/ui/button";
import { cn } from "@/lib/utils";
import { JournalLogLine } from "./JournalLogLine";

const DEFAULT_ROW_HEIGHT = 20;
const OVERSCAN = 24;
const PIN_THRESHOLD_PX = 40;

function ScrollSpacer({ height }: { height: number }) {
  if (height <= 0) return null;
  return (
    <div aria-hidden style={{ height, lineHeight: 0, flexShrink: 0 }} />
  );
}

interface JournalVirtualizedLogProps {
  entries: AppJournalEntry[];
  emptyMessage?: string;
  rowHeight?: number;
  className?: string;
  /** When true, new entries scroll the view to the bottom if already pinned. */
  stickToBottom?: boolean;
}

export function JournalVirtualizedLog({
  entries,
  emptyMessage = "No entries found",
  rowHeight = DEFAULT_ROW_HEIGHT,
  className,
  stickToBottom = true,
}: JournalVirtualizedLogProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isPinnedRef = useRef(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const rowCount = entries.length;
  const lastEntryId = entries[rowCount - 1]?.id;

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: OVERSCAN,
    getItemKey: (index) => entries[index]?.id ?? index,
  });

  useLayoutEffect(() => {
    virtualizer.measure();
  }, [rowCount, rowHeight, virtualizer]);

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    isPinnedRef.current = true;
    setShowJumpToLatest(false);
  }, []);

  useLayoutEffect(() => {
    if (!stickToBottom || !isPinnedRef.current) return;
    scrollToBottom();
  }, [rowCount, lastEntryId, stickToBottom, scrollToBottom]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const pinned =
      el.scrollHeight - el.scrollTop - el.clientHeight < PIN_THRESHOLD_PX;
    isPinnedRef.current = pinned;
    setShowJumpToLatest(!pinned && rowCount > 0);
  };

  const items = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = items.length > 0 ? items[0].start : 0;
  const paddingBottom =
    items.length > 0 ? totalSize - items[items.length - 1].end : 0;

  return (
    <div className={cn("relative flex-1 min-h-0", className)}>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="h-full min-h-0 overflow-x-auto overflow-y-auto bg-background"
      >
        {rowCount === 0 ? (
          <div className="flex h-32 items-center justify-center">
            <div className="text-center text-muted-foreground">
              <BookOpen className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p className="text-xs">{emptyMessage}</p>
            </div>
          </div>
        ) : (
          <>
            <ScrollSpacer height={paddingTop} />
            {items.map((virtualRow) => {
              const entry = entries[virtualRow.index];
              if (!entry) return null;
              return (
                <JournalLogLine
                  key={virtualRow.key}
                  entry={entry}
                  className="w-full"
                />
              );
            })}
            <ScrollSpacer height={paddingBottom} />
          </>
        )}
      </div>
      {showJumpToLatest && (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="absolute bottom-2 right-2 h-6 px-2 text-[10px] shadow-md"
          onClick={scrollToBottom}
        >
          ↓ Latest
        </Button>
      )}
    </div>
  );
}
